import React, { useMemo, useState } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  ReferenceLine,
} from "recharts";

/**
 * NOTE: This page uses illustrative mock data shaped like BLS/O*NET outputs so you
 * can wire in live series later. The layout is production-ready and SEO optimized.
 */

// ---------- helpers ----------
const currency = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const pct = (x: number) => `${x.toFixed(1)}%`;

// build last N years
function buildYears(n: number) {
  const end = new Date().getFullYear();
  const start = end - (n - 1);
  return Array.from({ length: n }, (_, i) => start + i);
}

// compound growth series for an industry (index base = 100 at first year)
function seriesFromRate(years: number[], annualRate: number) {
  const out: number[] = [];
  let idx = 100;
  years.forEach((_, i) => {
    if (i === 0) out.push(idx);
    else {
      idx = idx * (1 + annualRate);
      out.push(idx);
    }
  });
  return out;
}

// ---------- mock data (swap with BLS/O*NET later) ----------
const INDUSTRIES = [
  { key: "tech", name: "Technology", rate: 0.030 },
  { key: "health", name: "Healthcare", rate: 0.025 },
  { key: "construction", name: "Construction", rate: 0.012 },
  { key: "logistics", name: "Logistics", rate: 0.009 },
  { key: "retail", name: "Retail", rate: -0.005 },
  { key: "manufacturing", name: "Manufacturing", rate: -0.010 },
];

const JOB_PAY = [
  { title: "Software Engineer", median: 115_000, p25: 90_000, p75: 145_000, req: "Bachelor’s" },
  { title: "Registered Nurse", median: 82_000, p25: 70_000, p75: 100_000, req: "Associate/Bachelor’s" },
  { title: "Data Analyst", median: 85_000, p25: 70_000, p75: 110_000, req: "Bachelor’s" },
  { title: "Electrician", median: 64_000, p25: 52_000, p75: 85_000, req: "Apprenticeship/Certificate" },
  { title: "Construction Manager", median: 98_000, p25: 80_000, p75: 130_000, req: "Bachelor’s" },
  { title: "Retail Supervisor", median: 45_000, p25: 36_000, p75: 58_000, req: "No specific degree" },
];

const DEGREE_MIX = [
  { band: "No specific degree", share: 0.18 },
  { band: "Certificate/Apprenticeship", share: 0.22 },
  { band: "Associate", share: 0.16 },
  { band: "Bachelor’s", share: 0.34 },
  { band: "Graduate/Professional", share: 0.10 },
];

// ---------- page ----------
export default function Trends() {
  const [yearsBack, setYearsBack] = useState(10); // “last 10 years” default
  const years = useMemo(() => buildYears(Math.max(5, Math.min(20, Math.round(yearsBack)))), [yearsBack]);

  // build industry employment index panel
  const industryLines = useMemo(() => {
    const rows = years.map((y) => ({ year: y } as any));
    INDUSTRIES.forEach((ind) => {
      const s = seriesFromRate(years, ind.rate);
      s.forEach((v, i) => {
        rows[i][ind.key] = Number(v.toFixed(1));
      });
    });
    return rows;
  }, [years]);

  // compute 10-year growth for table
  const growthTable = useMemo(() => {
    return INDUSTRIES.map((ind) => {
      const first = industryLines[0][ind.key];
      const last = industryLines[industryLines.length - 1][ind.key];
      const growth = (last / first - 1) * 100;
      return { ...ind, growth, first, last };
    })
      .sort((a, b) => b.growth - a.growth);
  }, [industryLines]);

  // degree bar data
  const degreeBars = DEGREE_MIX.map((d) => ({ band: d.band, pct: +(d.share * 100).toFixed(1) }));

  // SEO JSON-LD
  const canonical = "https://free1099calc.com/trends";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Job Trends (Last 10 Years)",
    "description": "Industries growing/declining, typical pay by job title, and education requirements.",
    "url": canonical,
    "about": ["Employment trends", "Industries", "Salary", "Education requirements"],
    "isPartOf": { "@type": "WebSite", "name": "Free1099Calc", "url": "https://free1099calc.com" },
  };

  return (
    <>
      <Helmet>
        <title>Job Trends & Industry Growth Analysis - Last 10 Years | Free1099Calc</title>
        <meta
          name="description"
          content="Explore job trends, industry growth rates, salary data, and education requirements over the last decade. Compare tech, healthcare, manufacturing, and freelance gig economy sectors."
        />
        <meta name="keywords" content="job trends, industry growth, salary data, employment statistics, tech jobs, healthcare jobs, gig economy, freelance trends, BLS data, career planning" />
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Job Trends & Industry Growth Analysis - Last 10 Years" />
        <meta property="og:description" content="Discover which industries are growing, average salaries, and education requirements to plan your freelance career." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/trends" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Job Trends & Industry Growth - 10 Year Analysis" />
        <meta name="twitter:description" content="Explore tech, healthcare, manufacturing, and gig economy job trends with salary and growth data." />
        
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-5 py-2.5 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full mb-6">
              <span className="text-white text-sm font-bold">📊 MARKET INSIGHTS</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Job Trends & Industry Growth
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              A clear look at industry momentum, pay ranges, and education requirements to plan your freelance career.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Calculate Your Taxes →
              </Link>
              <Link
                to="/about"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>


    <main className="mx-auto max-w-6xl px-4 md:px-6 py-8">

      {/* Expense Tracker Banner */}
      <div className="mb-8">
        <ExpenseTrackerBanner compact />
      </div>

      {/* Controls */}
      <section className="rounded-3xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-8 md:p-10 shadow-xl">
        <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
            </svg>
          </div>
          View settings
        </h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          <NumberField
            label="Years to show (5–20)"
            value={yearsBack}
            min={5}
            max={20}
            onChange={setYearsBack}
          />
          <FieldHelp>
            Industry lines use an index (100 = first year shown). Growth table compares first vs. last year.
          </FieldHelp>
        </div>
      </section>

      {/* Industry trend lines */}
      <section className="mt-8 rounded-3xl border-2 border-blue-200 bg-white p-6 md:p-8 shadow-xl">
        <div className="mb-6">
          <h2 className="text-2xl font-black text-gray-900 mb-2">Industries: employment index</h2>
          <p className="text-base text-gray-600">
            Indexed to 100 in the first visible year. Illustrative rates; replace with BLS CES/OOH series when ready.
          </p>
        </div>
        <div className="h-80 w-full">
          <ResponsiveContainer>
            <LineChart data={industryLines}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              {INDUSTRIES.map((ind) => (
                <Line
                  key={ind.key}
                  dataKey={ind.key}
                  name={ind.name}
                  type="monotone"
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Growth & decline table */}
      <section className="mt-8 grid md:grid-cols-2 gap-8">
        <div className="rounded-3xl border-2 border-green-200 bg-white overflow-hidden shadow-xl">
          <div className="p-6 bg-gradient-to-r from-green-600 to-emerald-600">
            <h2 className="text-2xl font-black text-white">Top industry growers</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Industry</th>
                <th className="text-right px-3 py-2">Growth</th>
              </tr>
            </thead>
            <tbody>
              {growthTable.slice(0, 3).map((g, i) => (
                <tr key={g.key} className={i % 2 ? "bg-slate-50/50" : "bg-white"}>
                  <td className="px-3 py-2">{g.name}</td>
                  <td className="px-3 py-2 text-right font-medium">{pct(g.growth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-3xl border-2 border-red-200 bg-white overflow-hidden shadow-xl">
          <div className="p-6 bg-gradient-to-r from-red-600 to-orange-600">
            <h2 className="text-2xl font-black text-white">Industries in decline</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Industry</th>
                <th className="text-right px-3 py-2">Change</th>
              </tr>
            </thead>
            <tbody>
              {growthTable.slice(-3).reverse().map((g, i) => (
                <tr key={g.key} className={i % 2 ? "bg-slate-50/50" : "bg-white"}>
                  <td className="px-3 py-2">{g.name}</td>
                  <td className="px-3 py-2 text-right font-medium">{pct(g.growth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pay by job title */}
      <section className="mt-8 rounded-3xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-8 md:p-10 shadow-xl">
        <h2 className="text-2xl font-black text-gray-900 mb-6">Typical pay by job title</h2>
        <p className="text-base text-gray-700 mb-6">
          Use this as a directional guide. For official data, see{" "}
          <a
            href="https://www.bls.gov/oes/tables.htm"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-slate-300 hover:decoration-slate-900"
          >
            BLS Occupational Employment and Wage Statistics
          </a>
          .
        </p>

        <div className="space-y-4">
          {JOB_PAY.map((j, i) => {
            const max = Math.max(...JOB_PAY.map((x) => x.p75));
            const w = (val: number) => `${(val / max) * 100}%`;
            return (
              <div key={j.title} className={i % 2 ? "bg-slate-50/50 rounded-lg p-3" : "p-3"}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">{j.title}</div>
                  <div className="text-sm text-slate-600">{j.req}</div>
                </div>
                <div className="mt-2">
                  <div className="h-3 w-full rounded bg-slate-100 relative">
                    {/* p25 to p75 band */}
                    <div
                      className="absolute top-0 h-3 rounded bg-slate-300"
                      style={{ left: w(j.p25), width: `calc(${w(j.p75)} - ${w(j.p25)})` }}
                      aria-hidden="true"
                    />
                    {/* median marker */}
                    <div
                      className="absolute top-0 h-3 w-0.5 bg-slate-900"
                      style={{ left: w(j.median) }}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-600">
                    <span>P25 {currency(j.p25)}</span>
                    <span>Median {currency(j.median)}</span>
                    <span>P75 {currency(j.p75)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Education / credential mix */}
      <section className="mt-6 rounded-2xl border bg-white p-4 md:p-6">
        <div className="mb-3">
          <h2 className="text-xl font-semibold">Education & credential mix</h2>
          <p className="text-sm text-slate-600">
            A rough cross-industry mix. For role-specific requirements, consult{" "}
            <a
              href="https://www.onetonline.org/find/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-slate-300 hover:decoration-slate-900"
            >
              O*NET
            </a>{" "}
            or the{" "}
            <a
              href="https://www.bls.gov/ooh/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-slate-300 hover:decoration-slate-900"
            >
              BLS Occupational Outlook Handbook
            </a>
            .
          </p>
        </div>

        <div className="h-72 w-full">
          <ResponsiveContainer>
            <BarChart data={degreeBars}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="band" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: any) => `${v}%`} />
              <Legend />
              <ReferenceLine y={50} strokeDasharray="4 4" />
              <Bar dataKey="pct" name="Share of roles (%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Learn more / internal links */}
      <section className="mt-10 rounded-2xl border bg-white p-6 md:p-8">
        <h2 className="text-2xl font-bold mb-4">Understanding Job Market Trends for Freelancers</h2>
        
        <div className="space-y-6 text-slate-700">
          <div>
            <h3 className="text-xl font-semibold mb-3">Why Job Trends Matter for 1099 Workers</h3>
            <p className="mb-4">
              As a <strong>freelancer, independent contractor, or consultant</strong>, understanding job market trends 
              is crucial for strategic career planning. Unlike traditional employees who rely on a single employer, 
              1099 workers must constantly evaluate market demand, pricing trends, and emerging opportunities across 
              industries. This data helps you:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Price your services competitively:</strong> Understanding salary trends in your field helps you set hourly or project rates that reflect market value</li>
              <li><strong>Identify growing sectors:</strong> Target industries with strong growth trajectories for more stable, long-term client relationships</li>
              <li><strong>Plan skill development:</strong> Invest in training and certifications in areas with high demand and strong compensation</li>
              <li><strong>Diversify intelligently:</strong> Balance high-paying but declining industries with emerging sectors to manage risk</li>
              <li><strong>Negotiate effectively:</strong> Use labor market data to justify rate increases with existing clients</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">Key Industries for Freelancers</h3>
            <p className="mb-4">The chart above shows illustrative growth trends across major industry sectors. Here's what freelancers should know about each:</p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-600 pl-4">
                <h4 className="font-semibold mb-2">Technology & Software Development</h4>
                <p className="text-sm mb-2">
                  <strong>Growth:</strong> Strong and accelerating with AI, cloud computing, and cybersecurity driving demand. 
                  <strong>Opportunities:</strong> Web development, mobile apps, DevOps, data science, machine learning, and blockchain.
                  <strong>Typical Rates:</strong> $75-200/hour depending on specialization and experience.
                </p>
              </div>
              
              <div className="border-l-4 border-green-600 pl-4">
                <h4 className="font-semibold mb-2">Healthcare & Telemedicine</h4>
                <p className="text-sm mb-2">
                  <strong>Growth:</strong> Steady expansion driven by aging population and telehealth adoption. 
                  <strong>Opportunities:</strong> Medical billing, health IT consulting, telemedicine platform support, healthcare content writing.
                  <strong>Typical Rates:</strong> $50-150/hour for specialized healthcare consulting.
                </p>
              </div>
              
              <div className="border-l-4 border-purple-600 pl-4">
                <h4 className="font-semibold mb-2">Creative & Digital Marketing</h4>
                <p className="text-sm mb-2">
                  <strong>Growth:</strong> Moderate growth as businesses shift to digital-first strategies. 
                  <strong>Opportunities:</strong> Graphic design, video editing, content marketing, SEO, social media management, copywriting.
                  <strong>Typical Rates:</strong> $40-125/hour depending on niche and portfolio.
                </p>
              </div>
              
              <div className="border-l-4 border-yellow-600 pl-4">
                <h4 className="font-semibold mb-2">Professional Services & Consulting</h4>
                <p className="text-sm mb-2">
                  <strong>Growth:</strong> Stable with specialization becoming more important. 
                  <strong>Opportunities:</strong> Business consulting, project management, financial planning, HR consulting, legal services.
                  <strong>Typical Rates:</strong> $100-300/hour for experienced consultants with specialized expertise.
                </p>
              </div>
              
              <div className="border-l-4 border-red-600 pl-4">
                <h4 className="font-semibold mb-2">Manufacturing & Retail</h4>
                <p className="text-sm mb-2">
                  <strong>Growth:</strong> Declining traditional roles but opportunities in automation, e-commerce, and supply chain optimization. 
                  <strong>Opportunities:</strong> E-commerce consulting, industrial automation, inventory management systems, logistics optimization.
                  <strong>Typical Rates:</strong> $50-125/hour for specialized industrial consulting.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">How AI is Reshaping the Freelance Landscape</h3>
            <p className="mb-4">
              <strong>Artificial Intelligence</strong> is dramatically transforming the types of work available to freelancers, 
              creating both challenges and opportunities:
            </p>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Roles Being Automated or Reduced:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Basic data entry and transcription (AI tools now handle 80%+ accuracy)</li>
                <li>Simple graphic design tasks (Canva, DALL-E, Midjourney reducing demand for basic designs)</li>
                <li>Basic copywriting and content generation (ChatGPT, Claude handling first drafts)</li>
                <li>Entry-level coding tasks (GitHub Copilot, Cursor AI assisting developers)</li>
                <li>Basic bookkeeping and data categorization (AI-powered accounting software)</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">New High-Value Opportunities Created by AI:</h4>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li><strong>AI prompt engineering:</strong> Crafting effective prompts for large language models ($50-150/hour)</li>
                <li><strong>AI integration consulting:</strong> Helping businesses implement AI tools into workflows ($100-250/hour)</li>
                <li><strong>AI content editing and fact-checking:</strong> Refining AI-generated content for accuracy and brand voice ($40-100/hour)</li>
                <li><strong>Machine learning model training:</strong> Labeling data and training custom AI models ($30-80/hour)</li>
                <li><strong>AI ethics consulting:</strong> Advising on responsible AI implementation and bias mitigation ($150-300/hour)</li>
                <li><strong>Automation workflow design:</strong> Building AI-powered automation using tools like Make, Zapier, n8n ($75-200/hour)</li>
              </ul>
            </div>
            
            <p className="mb-4">
              <strong>Key Strategy:</strong> Rather than competing with AI, position yourself as someone who <em>leverages AI</em> 
              to deliver better results faster. Freelancers who become "AI-augmented" can command premium rates by combining 
              human creativity, judgment, and domain expertise with AI-powered efficiency.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">Strategic Career Planning for Freelancers</h3>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li>
                <strong>Assess your current position:</strong> Compare your rates and skills to market data. Are you underpricing? 
                Is your specialization in a growing or declining sector?
              </li>
              <li>
                <strong>Identify adjacent opportunities:</strong> Look for skills that complement your current expertise. 
                For example, web developers can learn SEO, designers can learn UX research, writers can learn email marketing automation.
              </li>
              <li>
                <strong>Build a learning roadmap:</strong> Invest 5-10 hours per week in skill development. Prioritize skills 
                that are (a) in high demand, (b) complement your existing expertise, and (c) are not easily automated.
              </li>
              <li>
                <strong>Diversify your client base:</strong> Avoid over-reliance on a single industry or client. Aim for 
                clients across 2-3 different sectors to hedge against industry-specific downturns.
              </li>
              <li>
                <strong>Track your own metrics:</strong> Monitor your average hourly rate, utilization (billable hours as % of 
                total hours), client acquisition cost, and lifetime client value. Treat your freelance practice like a business.
              </li>
              <li>
                <strong>Plan for transitions:</strong> If your primary skill is at risk of automation, start building adjacent 
                skills now. Give yourself 6-12 months to gradually shift your positioning before market conditions force a change.
              </li>
            </ol>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">Using This Data to Negotiate Rate Increases</h3>
            <p className="mb-4">
              When it's time to raise your rates with existing clients, labor market data strengthens your negotiation position:
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
              <p className="text-sm italic mb-2">
                "Based on current market trends, professionals with my skillset in [industry] are seeing average compensation 
                of $X-Y per hour. Given the [inflation rate / increased demand / specialized expertise I've developed], I'd 
                like to discuss adjusting my rate from $A to $B, effective [date]. This keeps our arrangement competitive 
                with market conditions while ensuring I can continue delivering the same high-quality work you've come to expect."
              </p>
            </div>
            <p className="text-sm">
              Most clients understand and respect data-driven rate adjustments, especially when you frame it around maintaining 
              quality and market competitiveness rather than personal financial needs.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">Additional Resources for Career Planning</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">
                  BLS Occupational Outlook Handbook
                </a> - Detailed career profiles with growth projections, education requirements, and median pay
              </li>
              <li>
                <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">
                  O*NET Online
                </a> - Comprehensive database of occupational requirements, skills, and job market trends
              </li>
              <li>
                <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">
                  BLS Occupational Employment and Wage Statistics
                </a> - Official wage data by occupation, industry, and geographic area
              </li>
              <li>
                <Link to="/" className="text-primary-600 underline hover:text-primary-700">
                  Free1099Calc Tax Calculator
                </Link> - Estimate your tax burden to set rates that account for self-employment taxes
              </li>
              <li>
                <Link to="/retirement" className="text-primary-600 underline hover:text-primary-700">
                  Retirement Calculator
                </Link> - Model long-term savings goals to determine required income levels
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-600 p-4 rounded mt-6">
            <p className="text-sm text-slate-800">
              <strong>Disclaimer:</strong> The job trend data shown on this page is illustrative and generated for educational 
              purposes. Some content was created with AI assistance and reviewed for accuracy. For official labor statistics 
              and career guidance, consult the Bureau of Labor Statistics (BLS), O*NET, and industry-specific professional 
              associations. Always verify market data from authoritative sources before making career or pricing decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="mt-8 rounded-2xl border bg-gradient-to-br from-primary-50 to-accent-50 p-6 md:p-8">
        <h2 className="text-2xl font-bold mb-4">Plan Your Freelance Career</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-2">Tax Calculator</h3>
            <p className="text-sm text-slate-600">Price your work to account for self-employment taxes and estimated payments</p>
          </Link>
          <Link to="/retirement" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-2">Retirement Planning</h3>
            <p className="text-sm text-slate-600">Model long-term savings with inflation-adjusted returns</p>
          </Link>
          <Link to="/calculators/inflation" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-2">Inflation Impact</h3>
            <p className="text-sm text-slate-600">Understand how rising costs affect your rates and purchasing power</p>
          </Link>
        </div>
      </section>

      <RecentPosts />
    </main>
    </>
  );
}

/* ---------- light UI helpers to match the rest of your site ---------- */

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  const [raw, setRaw] = useState<string>(String(value));
  React.useEffect(() => {
    const v = String(value);
    if (v !== raw) setRaw(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const clampLocal = (n: number) => {
    let x = n;
    if (typeof min === "number") x = Math.max(min, x);
    if (typeof max === "number") x = Math.min(max, x);
    return x;
  };

  return (
    <label className="block">
      <div className="text-sm font-medium text-slate-800 mb-1">{label}</div>
      <input
        type="text"
        inputMode="numeric"
        className="w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-slate-300"
        value={raw}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
          setRaw(next);
          const n = Number(next);
          if (Number.isFinite(n)) onChange(n);
        }}
        onBlur={() => {
          const n = Number(raw);
          const clamped = Number.isFinite(n) ? clampLocal(n) : (typeof min === "number" ? min : 0);
          setRaw(String(clamped));
          onChange(clamped);
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}

function FieldHelp({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-slate-600">{children}</div>;
}
